import type { components } from '@connected/api-client/api.generated';
import type { ZodValidator } from '@tanstack/zod-form-adapter';

import { validators } from '@connected/api-client/generated/validators.generated';
import { usePatcher } from '@connected/api-client/hooks/use-patcher';
import { toPascalCase } from '@connected/api-client/lib/utils';
import { elektraAppQueryOptions } from '@connected/elektra-sdk/queries/query-options';
import FieldInfo from '@connected/form-fields/components/field-info';
import { Label } from '@connected/form-fields/components/label';
import { NumberField } from '@connected/form-fields/components/number';
import {
    SingleHorizontalOptionItem,
    SingleOptionField,
} from '@connected/form-fields/components/option';
import { Checkbox } from '@connected/ui/components/checkbox';
import { Section } from '@connected/ui/features/layouts';
import { useForm } from '@tanstack/react-form';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-form-adapter';
import { useTranslation } from 'react-i18next';

import { ObservationComponent } from '@/components/features/observation';
import { FooterNavigation } from '@/components/navigation/footer-navigation';
import { logger } from '@/lib/logger';
import { queryClient } from '@/lib/query-client';

export const Route = createFileRoute(
    '/(auth)/projects/$projectVersionId/re-inspections/distributionboards/$distributionboardId/board/measurements/',
)({
    component: DistributionboardMeasurementsPage,
    loader: ({ context, params }) => {
        context.queryClient.ensureQueryData(elektraAppQueryOptions.project.inspection.distributionBoard.distributionBoardOptions({ electricalProjectDistributionBoardId: params.distributionboardId }));
        context.queryClient.ensureQueryData(elektraAppQueryOptions.project.inspection.information.inspectionsEditOptions({ electricalProjectVersionId: params.projectVersionId }));
    },
});

type PowerFormValues = {
    powerPhaseRotation: string;
    currentL1: number | null;
    currentL2: number | null;
    currentL3: number | null;
    currentNeutral: number | null;
};

type VoltageFormValues = {
    voltageL1: number | null;
    voltageL2: number | null;
    voltageL3: number | null;
    voltageL1Neutral: number | null;
    voltageL2Neutral: number | null;
    voltageL3Neutral: number | null;
    voltageL1Ground: number | null;
    voltageL2Ground: number | null;
    voltageL3Ground: number | null;
    voltageNeutralGround: number | null;
};

type CircuitimpedantiesFormValues = {
    groundImpedanceCurrentL1: number | null;
    groundImpedanceCurrentL2: number | null;
    groundImpedanceCurrentL3: number | null;
    groundImpedanceL1: number | null;
    groundImpedanceL2: number | null;
    groundImpedanceL3: number | null;
};

type NetimpedantiesFormValues = {
    neutralImpedanceCurrentL1: number | null;
    neutralImpedanceCurrentL2: number | null;
    neutralImpedanceCurrentL3: number | null;
    neutralImpedanceL1: number | null;
    neutralImpedanceL2: number | null;
    neutralImpedanceL3: number | null;
    measureImpedanceBetweenPowerPhases: boolean | null;
    impedanceCurrentL1L2: number | null;
    impedanceCurrentL1L3: number | null;
    impedanceCurrentL2L3: number | null;
    impedanceL1L2: number | null;
    impedanceL1L3: number | null;
    impedanceL2L3: number | null;
};

function DistributionboardMeasurementsPage() {
    const { t } = useTranslation('re-inspections');
    const { t: c } = useTranslation('common');
    const params = Route.useParams();

    const { data } = useQuery(elektraAppQueryOptions.project.inspection.information.inspectionsEditOptions({ electricalProjectVersionId: params.projectVersionId }));
    const { data: distributionBoardData } = useQuery(elektraAppQueryOptions.project.inspection.distributionBoard.distributionBoardOptions({ electricalProjectDistributionBoardId: params.distributionboardId }));

    const patcher = usePatcher('/api/v2/electrical/projects/distributionboards/patch', {
        onMutate: async (variables) => {
            const body = toPascalCase(variables!.body!);

            const distributionBoardQueryOptions = elektraAppQueryOptions.project.inspection.distributionBoard.distributionBoardOptions({ electricalProjectDistributionBoardId: params.distributionboardId });

            await queryClient.cancelQueries(distributionBoardQueryOptions);

            const previousDistributionBoardData = queryClient.getQueryData(distributionBoardQueryOptions.queryKey) as components['schemas']['EditElectricalProjectDistributionBoardQueryResult'];
            const newDistributionBoardData: components['schemas']['EditElectricalProjectDistributionBoardQueryResult'] = {
                ...previousDistributionBoardData,
                DistributionBoard: {
                    ...previousDistributionBoardData.DistributionBoard,
                    ...body,
                },
            };

            queryClient.setQueryData(distributionBoardQueryOptions.queryKey, newDistributionBoardData);

            return {
                previousData: previousDistributionBoardData,
                queryKey: distributionBoardQueryOptions.queryKey,
            };
        },
        onSettled: async (_data, _variables, context: any) => {
            await queryClient.invalidateQueries({ queryKey: context.queryKey });
        },
        scope: {
            id: params.projectVersionId,
        },
    }, {
        body: {
            ElectricalProjectDistributionBoardId: params.distributionboardId,
        },
    });

    // /api/v2/electrical/projects/distributionboards/edit
    return (data?.Inspection?.DistributionBoardMeasurementRegistrationKind !== 'Input' && data?.Inspection?.DistributionBoardMeasurementRegistrationKind !== undefined)
        ? (
                <Section>
                    <Section.Header>
                        <Section.Header.Title>Meetwaardes</Section.Header.Title>
                    </Section.Header>
                    <Section.Content>
                        <div className="flex flex-row justify-center gap-4">
                            <p>Meetwaardes zijn opvraagbaar</p>
                        </div>
                    </Section.Content>
                    <Footer />
                </Section>
            )
        : (
                <>
                    <Section>
                        <Section.Header className="flex-row justify-between items-center">
                            <Section.Header.Title>
                                {t('distribution-box.measurements.navigation.circuitimpedanties')}
                            </Section.Header.Title>
                            <label className="flex items-center gap-2 text-sm font-normal cursor-pointer">
                                <Checkbox
                                    checked={distributionBoardData?.DistributionBoard?.ReportGroundImpedanceMeasurements !== false}
                                    onCheckedChange={(checked) => {
                                        patcher.patchField('ReportGroundImpedanceMeasurements')({ value: checked });
                                    }}
                                />
                                {c('report.include-in-report')}
                            </label>
                        </Section.Header>
                        <Section.Content>
                            <CircuitimpedantiesForm powerPhaseKind={distributionBoardData?.DistributionBoard?.PowerPhaseKind} />
                        </Section.Content>
                    </Section>
                    <Section>
                        <Section.Header className="flex-row justify-between items-center">
                            <Section.Header.Title>
                                {t('distribution-box.measurements.navigation.netimpedanties')}
                            </Section.Header.Title>
                            <label className="flex items-center gap-2 text-sm font-normal cursor-pointer">
                                <Checkbox
                                    checked={distributionBoardData?.DistributionBoard?.ReportNeutralImpedanceMeasurements !== false}
                                    onCheckedChange={(checked) => {
                                        patcher.patchField('ReportNeutralImpedanceMeasurements')({ value: checked });
                                    }}
                                />
                                {c('report.include-in-report')}
                            </label>
                        </Section.Header>
                        <Section.Content>
                            <NetimpedantiesForm powerPhaseKind={distributionBoardData?.DistributionBoard?.PowerPhaseKind} />
                        </Section.Content>
                    </Section>
                    <Section>
                        <Section.Header className="flex-row justify-between items-center">
                            <Section.Header.Title>
                                {t('distribution-box.measurements.navigation.power')}
                            </Section.Header.Title>
                            <label className="flex items-center gap-2 text-sm font-normal cursor-pointer">
                                <Checkbox
                                    checked={distributionBoardData?.DistributionBoard?.ReportCurrentMeasurements !== false}
                                    onCheckedChange={(checked) => {
                                        patcher.patchField('ReportCurrentMeasurements')({ value: checked });
                                    }}
                                />
                                {c('report.include-in-report')}
                            </label>
                        </Section.Header>
                        <Section.Content>
                            <PowerForm powerPhaseKind={distributionBoardData?.DistributionBoard?.PowerPhaseKind} />
                        </Section.Content>
                    </Section>
                    <Section>
                        <Section.Header className="flex-row justify-between items-center">
                            <Section.Header.Title>
                                {t('distribution-box.measurements.navigation.voltage')}
                            </Section.Header.Title>
                            <label className="flex items-center gap-2 text-sm font-normal cursor-pointer">
                                <Checkbox
                                    checked={distributionBoardData?.DistributionBoard?.ReportVoltageMeasurements !== false}
                                    onCheckedChange={(checked) => {
                                        patcher.patchField('ReportVoltageMeasurements')({ value: checked });
                                    }}
                                />
                                {c('report.include-in-report')}
                            </label>
                        </Section.Header>
                        <Section.Content>
                            <VoltageForm powerPhaseKind={distributionBoardData?.DistributionBoard?.PowerPhaseKind} />
                        </Section.Content>
                        <Footer />
                    </Section>
                </>
            );
}

function PowerForm({ powerPhaseKind }: { powerPhaseKind: string | null | undefined }) {
    const { t } = useTranslation('re-inspections');
    const isThreePhase = powerPhaseKind === '3';

    const params = Route.useParams();

    const { data } = useQuery(
        elektraAppQueryOptions.project.inspection.distributionBoard.distributionBoardOptions({ electricalProjectDistributionBoardId: params.distributionboardId }),
    );

    const patcher = usePatcher('/api/v2/electrical/projects/distributionboards/patch', {
        onMutate: async (variables) => {
            const body = toPascalCase(variables!.body!);

            const distributionBoardQueryOptions = elektraAppQueryOptions.project.inspection.distributionBoard.distributionBoardOptions({ electricalProjectDistributionBoardId: params.distributionboardId });

            await queryClient.cancelQueries(distributionBoardQueryOptions);

            const previousDistributionBoardData = queryClient.getQueryData(distributionBoardQueryOptions.queryKey) as components['schemas']['EditElectricalProjectDistributionBoardQueryResult'];
            const newDistributionBoardData: components['schemas']['EditElectricalProjectDistributionBoardQueryResult'] = {
                ...previousDistributionBoardData,
                DistributionBoard: {
                    ...previousDistributionBoardData.DistributionBoard,
                    ...body,
                },
            };

            logger.debug({
                message: 'onMutate(distributionBoardQueryOptions)',
                path: '/api/v2/electrical/projects/distributionboards/patch',
                distributionBoard: {
                    previousData: previousDistributionBoardData,
                    newData: newDistributionBoardData,
                    queryKey: distributionBoardQueryOptions.queryKey,
                    body,
                },
            });

            queryClient.setQueryData(distributionBoardQueryOptions.queryKey, newDistributionBoardData);

            return {
                previousData: previousDistributionBoardData,
                queryKey: distributionBoardQueryOptions.queryKey,
            };
        },
        onSettled: async (_data, _variables, context: any) => {
            await queryClient.invalidateQueries({ queryKey: context.queryKey });
        },
        scope: {
            id: params.projectVersionId,
        },
    }, {
        body: {
            ElectricalProjectDistributionBoardId: params.distributionboardId,
        },
    });

    const { Field } = useForm<PowerFormValues, ZodValidator>({
        defaultValues: {
            powerPhaseRotation: data?.DistributionBoard?.PowerPhaseRotation || '',
            currentL1: data?.DistributionBoard?.CurrentL1 || null,
            currentL2: data?.DistributionBoard?.CurrentL2 || null,
            currentL3: data?.DistributionBoard?.CurrentL3 || null,
            currentNeutral: data?.DistributionBoard?.CurrentNeutral || null,
        },
        validatorAdapter: zodValidator(),
    });

    return (
        <form className="flex flex-col gap-4">
            {isThreePhase && (
                <Field
                    name="powerPhaseRotation"
                    validators={{
                        onChange: validators.patchElectricalProjectDistributionBoardCommand.powerPhaseRotation,
                        onChangeAsync: patcher.patchField('PowerPhaseRotation'),
                        onChangeAsyncDebounceMs: patcher.patchField.debounceTime,
                    }}
                >
                    {({ handleChange, state, name }) => (
                        <Label label={t('distribution-box.measurements.form.turnignField.label')}>
                            <SingleOptionField
                                name={name}
                                handleChange={handleChange}
                                state={state}
                                className="flex flex-row items-center justify-between"
                                options={[
                                    { label: 'Links', value: 'CounterClockwise' },
                                    { label: 'Rechts', value: 'Clockwise' },
                                ]}
                                render={SingleHorizontalOptionItem}
                            />
                            <FieldInfo state={state} />
                        </Label>
                    )}
                </Field>
            )}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Field
                    name="currentL1"
                    validators={{
                        onChange: validators.patchElectricalProjectDistributionBoardCommand.currentL1,
                        onChangeAsync: patcher.patchField('CurrentL1'),
                        onChangeAsyncDebounceMs: patcher.patchField.debounceTime,
                    }}
                >
                    {({ handleChange, state, name }) => (
                        <Label className="grid grid-cols-3 items-center" label={t('distribution-box.measurements.form.L1_A.label')}>
                            <NumberField
                                className="col-span-2 w-full"
                                maxPrecision={0}
                                name={name}
                                onChange={handleChange}
                                value={state.value}
                                placeholder={t('distribution-box.measurements.form.L1_A.placeholder')}
                            />
                            <FieldInfo state={state} />
                        </Label>
                    )}
                </Field>
                {isThreePhase && (
                    <>
                        <Field
                            name="currentL2"
                            validators={{
                                onChange: validators.patchElectricalProjectDistributionBoardCommand.currentL2,
                                onChangeAsync: patcher.patchField('CurrentL2'),
                                onChangeAsyncDebounceMs: patcher.patchField.debounceTime,
                            }}
                        >
                            {({ handleChange, state, name }) => (
                                <Label className="grid grid-cols-3 items-center" label={t('distribution-box.measurements.form.L2_A.label')}>
                                    <NumberField
                                        className="col-span-2 w-full"
                                        maxPrecision={0}
                                        name={name}
                                        onChange={handleChange}
                                        value={state.value}
                                        placeholder={t('distribution-box.measurements.form.L2_A.placeholder')}
                                    />
                                    <FieldInfo state={state} />
                                </Label>
                            )}
                        </Field>
                        <Field
                            name="currentL3"
                            validators={{
                                onChange: validators.patchElectricalProjectDistributionBoardCommand.currentL3,
                                onChangeAsync: patcher.patchField('CurrentL3'),
                                onChangeAsyncDebounceMs: patcher.patchField.debounceTime,
                            }}
                        >
                            {({ handleChange, state, name }) => (
                                <Label className="grid grid-cols-3 items-center" label={t('distribution-box.measurements.form.L3_A.label')}>
                                    <NumberField
                                        className="col-span-2 w-full"
                                        maxPrecision={0}
                                        name={name}
                                        onChange={handleChange}
                                        value={state.value}
                                        placeholder={t('distribution-box.measurements.form.L3_A.placeholder')}
                                    />
                                    <FieldInfo state={state} />
                                </Label>
                            )}
                        </Field>
                    </>
                )}
                <Field
                    name="currentNeutral"
                    validators={{
                        onChange: validators.patchElectricalProjectDistributionBoardCommand.currentNeutral,
                        onChangeAsync: patcher.patchField('CurrentNeutral'),
                        onChangeAsyncDebounceMs: patcher.patchField.debounceTime,
                    }}
                >
                    {({ handleChange, state, name }) => (
                        <Label className="grid grid-cols-3 items-center" label={t('distribution-box.measurements.form.N_A.label')}>
                            <NumberField
                                className="col-span-2 w-full"
                                maxPrecision={0}
                                name={name}
                                onChange={handleChange}
                                value={state.value}
                                placeholder={t('distribution-box.measurements.form.N_A.placeholder')}
                            />
                            <FieldInfo state={state} />
                        </Label>
                    )}
                </Field>
            </div>
            <ObservationComponent
                electricalProjectVersionId={params.projectVersionId}
                objectKind="DistributionBoardCurrent"
                objectId={params.distributionboardId}
            />
        </form>
    );
}

function VoltageForm({ powerPhaseKind }: { powerPhaseKind: string | null | undefined }) {
    const { t } = useTranslation('re-inspections');
    const isThreePhase = powerPhaseKind === '3';

    const params = Route.useParams();

    const { data } = useQuery(elektraAppQueryOptions.project.inspection.distributionBoard.distributionBoardOptions({ electricalProjectDistributionBoardId: params.distributionboardId }));

    const patcher = usePatcher('/api/v2/electrical/projects/distributionboards/patch', {
        onMutate: (variables) => {
            const body = toPascalCase(variables!.body!);

            const distributionBoardQueryOptions = elektraAppQueryOptions.project.inspection.distributionBoard.distributionBoardOptions({ electricalProjectDistributionBoardId: params.distributionboardId });
            const previousDistributionBoardData = queryClient.getQueryData(distributionBoardQueryOptions.queryKey) as components['schemas']['EditElectricalProjectDistributionBoardQueryResult'];
            const newDistributionBoardData: components['schemas']['EditElectricalProjectDistributionBoardQueryResult'] = {
                ...previousDistributionBoardData,
                DistributionBoard: {
                    ...previousDistributionBoardData.DistributionBoard,
                    ...body,
                },
            };

            logger.debug({
                message: 'onMutate(distributionBoardQueryOptions)',
                path: '/api/v2/electrical/projects/distributionboards/patch',
                distributionBoard: {
                    previousData: previousDistributionBoardData,
                    newData: newDistributionBoardData,
                    queryKey: distributionBoardQueryOptions.queryKey,
                    body,
                },
            });

            queryClient.setQueryData(distributionBoardQueryOptions.queryKey, newDistributionBoardData);

            return {
                previousData: previousDistributionBoardData,
                queryKey: distributionBoardQueryOptions.queryKey,
            };
        },
        onSettled: async (_data, _variables, context: any) => {
            await queryClient.invalidateQueries({ queryKey: context.queryKey });
        },
        scope: {
            id: params.projectVersionId,
        },
    }, {
        body: {
            ElectricalProjectDistributionBoardId: params.distributionboardId,
        },
    });

    const { Field, handleSubmit } = useForm<VoltageFormValues, ZodValidator>({
        defaultValues: {
            voltageL1: data?.DistributionBoard?.VoltageL1 || null,
            voltageL2: data?.DistributionBoard?.VoltageL2 || null,
            voltageL3: data?.DistributionBoard?.VoltageL3 || null,
            voltageL1Neutral: data?.DistributionBoard?.VoltageL1Neutral || null,
            voltageL2Neutral: data?.DistributionBoard?.VoltageL2Neutral || null,
            voltageL3Neutral: data?.DistributionBoard?.VoltageL3Neutral || null,
            voltageL1Ground: data?.DistributionBoard?.VoltageL1Ground || null,
            voltageL2Ground: data?.DistributionBoard?.VoltageL2Ground || null,
            voltageL3Ground: data?.DistributionBoard?.VoltageL3Ground || null,
            voltageNeutralGround: data?.DistributionBoard?.VoltageNeutralGround || null,
        },
        validatorAdapter: zodValidator(),
    });

    async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault();
        await handleSubmit();
    }
    return (
        <form onSubmit={onSubmit} className="flex flex-col gap-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                {isThreePhase && (
                    <>
                        <Field
                            name="voltageL1"
                            validators={{
                                onChange: validators.patchElectricalProjectDistributionBoardCommand.voltageL1,
                                onChangeAsync: patcher.patchField('VoltageL1'),
                                onChangeAsyncDebounceMs: patcher.patchField.debounceTime,
                            }}
                        >
                            {({ handleChange, state, name }) => (
                                <Label className="grid grid-cols-3 items-center" label={t('distribution-box.measurements.form.L1_L1_2_V.label')}>
                                    <NumberField
                                        className="col-span-2 w-full"
                                        maxPrecision={0}
                                        name={name}
                                        onChange={handleChange}
                                        value={state.value}
                                        placeholder={t('distribution-box.measurements.form.L1_L1_2_V.placeholder')}
                                    />
                                    <FieldInfo state={state} />
                                </Label>
                            )}
                        </Field>
                        <Field
                            name="voltageL2"
                            validators={{
                                onChange: validators.patchElectricalProjectDistributionBoardCommand.voltageL2,
                                onChangeAsync: patcher.patchField('VoltageL2'),
                                onChangeAsyncDebounceMs: patcher.patchField.debounceTime,
                            }}
                        >
                            {({ handleChange, state, name }) => (
                                <Label className="grid grid-cols-3 items-center" label={t('distribution-box.measurements.form.L2_L1_3_V.label')}>
                                    <NumberField
                                        className="col-span-2 w-full"
                                        maxPrecision={0}
                                        name={name}
                                        onChange={handleChange}
                                        value={state.value}
                                        placeholder={t('distribution-box.measurements.form.L2_L1_3_V.placeholder')}
                                    />
                                    <FieldInfo state={state} />
                                </Label>
                            )}
                        </Field>
                        <Field
                            name="voltageL3"
                            validators={{
                                onChange:
                                    validators.patchElectricalProjectDistributionBoardCommand.voltageL3,
                                onChangeAsync: patcher.patchField('VoltageL3'),
                                onChangeAsyncDebounceMs: patcher.patchField.debounceTime,
                            }}
                        >
                            {({ handleChange, state, name }) => (
                                <Label className="grid grid-cols-3 items-center" label={t('distribution-box.measurements.form.L3_L2_3_V.label')}>
                                    <NumberField
                                        className="col-span-2 w-full"
                                        maxPrecision={0}
                                        name={name}
                                        onChange={handleChange}
                                        value={state.value}
                                        placeholder={t('distribution-box.measurements.form.L3_L2_3_V.placeholder')}
                                    />
                                    <FieldInfo state={state} />
                                </Label>
                            )}
                        </Field>
                    </>
                )}
                <Field
                    name="voltageL1Neutral"
                    validators={{
                        onChange: validators.patchElectricalProjectDistributionBoardCommand.voltageL1Neutral,
                        onChangeAsync: patcher.patchField('VoltageL1Neutral'),
                        onChangeAsyncDebounceMs: patcher.patchField.debounceTime,
                    }}
                >
                    {({ handleChange, state, name }) => (
                        <Label className="grid grid-cols-3 items-center" label={t('distribution-box.measurements.form.L1_N_V.label')}>
                            <NumberField
                                className="col-span-2 w-full"
                                maxPrecision={0}
                                name={name}
                                onChange={handleChange}
                                value={state.value}
                                placeholder={t('distribution-box.measurements.form.L1_N_V.placeholder')}
                            />
                            <FieldInfo state={state} />
                        </Label>
                    )}
                </Field>
                {isThreePhase && (
                    <>
                        <Field
                            name="voltageL2Neutral"
                            validators={{
                                onChange: validators.patchElectricalProjectDistributionBoardCommand.voltageL2Neutral,
                                onChangeAsync: patcher.patchField('VoltageL2Neutral'),
                                onChangeAsyncDebounceMs: patcher.patchField.debounceTime,
                            }}
                        >
                            {({ handleChange, state, name }) => (
                                <Label className="grid grid-cols-3 items-center" label={t('distribution-box.measurements.form.L2_N_V.label')}>
                                    <NumberField
                                        className="col-span-2 w-full"
                                        maxPrecision={0}
                                        name={name}
                                        onChange={handleChange}
                                        value={state.value}
                                        placeholder={t('distribution-box.measurements.form.L2_N_V.placeholder')}
                                    />
                                    <FieldInfo state={state} />
                                </Label>
                            )}
                        </Field>
                        <Field
                            name="voltageL3Neutral"
                            validators={{
                                onChange: validators.patchElectricalProjectDistributionBoardCommand.voltageL3Neutral,
                                onChangeAsync: patcher.patchField('VoltageL3Neutral'),
                                onChangeAsyncDebounceMs: patcher.patchField.debounceTime,
                            }}
                        >
                            {({ handleChange, state, name }) => (
                                <Label className="grid grid-cols-3 items-center" label={t('distribution-box.measurements.form.L3_N_V.label')}>
                                    <NumberField
                                        className="col-span-2 w-full"
                                        maxPrecision={0}
                                        name={name}
                                        onChange={handleChange}
                                        value={state.value}
                                        placeholder={t('distribution-box.measurements.form.L3_N_V.placeholder')}
                                    />
                                    <FieldInfo state={state} />
                                </Label>
                            )}
                        </Field>
                    </>
                )}
                <Field
                    name="voltageL1Ground"
                    validators={{
                        onChange: validators.patchElectricalProjectDistributionBoardCommand.voltageL1Ground,
                        onChangeAsync: patcher.patchField('VoltageL1Ground'),
                        onChangeAsyncDebounceMs: patcher.patchField.debounceTime,
                    }}
                >
                    {({ handleChange, state, name }) => (
                        <Label className="grid grid-cols-3 items-center" label={t('distribution-box.measurements.form.L1_PE_V.label')}>
                            <NumberField
                                className="col-span-2 w-full"
                                maxPrecision={0}
                                name={name}
                                onChange={handleChange}
                                value={state.value}
                                placeholder={t('distribution-box.measurements.form.L1_PE_V.placeholder')}
                            />
                            <FieldInfo state={state} />
                        </Label>
                    )}
                </Field>
                {isThreePhase && (
                    <>
                        <Field
                            name="voltageL2Ground"
                            validators={{
                                onChange: validators.patchElectricalProjectDistributionBoardCommand.voltageL2Ground,
                                onChangeAsync: patcher.patchField('VoltageL2Ground'),
                                onChangeAsyncDebounceMs: patcher.patchField.debounceTime,
                            }}
                        >
                            {({ handleChange, state, name }) => (
                                <Label className="grid grid-cols-3 items-center" label={t('distribution-box.measurements.form.L2_PE_V.label')}>
                                    <NumberField
                                        className="col-span-2 w-full"
                                        maxPrecision={0}
                                        name={name}
                                        onChange={handleChange}
                                        value={state.value}
                                        placeholder={t('distribution-box.measurements.form.L2_PE_V.placeholder')}
                                    />
                                    <FieldInfo state={state} />
                                </Label>
                            )}
                        </Field>
                        <Field
                            name="voltageL3Ground"
                            validators={{
                                onChange: validators.patchElectricalProjectDistributionBoardCommand.voltageL3Ground,
                                onChangeAsync: patcher.patchField('VoltageL3Ground'),
                                onChangeAsyncDebounceMs: patcher.patchField.debounceTime,
                            }}
                        >
                            {({ handleChange, state, name }) => (
                                <Label className="grid grid-cols-3 items-center" label={t('distribution-box.measurements.form.L3_PE_V.label')}>
                                    <NumberField
                                        className="col-span-2 w-full"
                                        maxPrecision={0}
                                        name={name}
                                        onChange={handleChange}
                                        value={state.value}
                                        placeholder={t('distribution-box.measurements.form.L3_PE_V.placeholder')}
                                    />
                                    <FieldInfo state={state} />
                                </Label>
                            )}
                        </Field>
                    </>
                )}
                <Field
                    name="voltageNeutralGround"
                    validators={{
                        onChange: validators.patchElectricalProjectDistributionBoardCommand.voltageNeutralGround,
                        onChangeAsync: patcher.patchField('VoltageNeutralGround'),
                        onChangeAsyncDebounceMs: patcher.patchField.debounceTime,
                    }}
                >
                    {({ handleChange, state, name }) => (
                        <Label className="grid grid-cols-3 items-center" label={t('distribution-box.measurements.form.N_PE_V.label')}>
                            <NumberField
                                className="col-span-2 w-full"
                                maxPrecision={0}
                                name={name}
                                onChange={handleChange}
                                value={state.value}
                                placeholder={t('distribution-box.measurements.form.N_PE_V.placeholder')}
                            />
                            <FieldInfo state={state} />
                        </Label>
                    )}
                </Field>
            </div>
            <ObservationComponent
                electricalProjectVersionId={params.projectVersionId}
                objectKind="DistributionBoardVoltage"
                objectId={params.distributionboardId}
            />
        </form>
    );
}

function CircuitimpedantiesForm({ powerPhaseKind }: { powerPhaseKind: string | null | undefined }) {
    const { t } = useTranslation('re-inspections');
    const isThreePhase = powerPhaseKind === '3';

    const params = Route.useParams();

    const { data } = useQuery(elektraAppQueryOptions.project.inspection.distributionBoard.distributionBoardOptions({ electricalProjectDistributionBoardId: params.distributionboardId }));

    const patcher = usePatcher('/api/v2/electrical/projects/distributionboards/patch', {
        onMutate: (variables) => {
            const body = toPascalCase(variables!.body!);

            const distributionBoardQueryOptions = elektraAppQueryOptions.project.inspection.distributionBoard.distributionBoardOptions({ electricalProjectDistributionBoardId: params.distributionboardId });
            const previousDistributionBoardData = queryClient.getQueryData(distributionBoardQueryOptions.queryKey) as components['schemas']['EditElectricalProjectDistributionBoardQueryResult'];
            const newDistributionBoardData: components['schemas']['EditElectricalProjectDistributionBoardQueryResult'] = {
                ...previousDistributionBoardData,
                DistributionBoard: {
                    ...previousDistributionBoardData.DistributionBoard,
                    ...body,
                },
            };

            logger.debug({
                message: 'onMutate(distributionBoardQueryOptions)',
                path: '/api/v2/electrical/projects/distributionboards/patch',
                distributionBoard: {
                    previousData: previousDistributionBoardData,
                    newData: newDistributionBoardData,
                    queryKey: distributionBoardQueryOptions.queryKey,
                    body,
                },
            });

            queryClient.setQueryData(distributionBoardQueryOptions.queryKey, newDistributionBoardData);

            return {
                previousData: previousDistributionBoardData,
                queryKey: distributionBoardQueryOptions.queryKey,
            };
        },
        onSettled: async (_data, _variables, context: any) => {
            await queryClient.invalidateQueries({ queryKey: context.queryKey });
        },
        scope: {
            id: params.projectVersionId,
        },
    }, {
        body: {
            ElectricalProjectDistributionBoardId: params.distributionboardId,
        },
    });

    const { Field, handleSubmit } = useForm<
        CircuitimpedantiesFormValues,
        ZodValidator
    >({
        defaultValues: {
            groundImpedanceCurrentL1: data?.DistributionBoard?.GroundImpedanceCurrentL1 || null,
            groundImpedanceCurrentL2: data?.DistributionBoard?.GroundImpedanceCurrentL2 || null,
            groundImpedanceCurrentL3: data?.DistributionBoard?.GroundImpedanceCurrentL3 || null,
            groundImpedanceL1: data?.DistributionBoard?.GroundImpedanceL1 || null,
            groundImpedanceL2: data?.DistributionBoard?.GroundImpedanceL2 || null,
            groundImpedanceL3: data?.DistributionBoard?.GroundImpedanceL3 || null,
        },
        validatorAdapter: zodValidator(),
    });

    async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault();
        await handleSubmit();
    }
    return (
        <form onSubmit={onSubmit} className="flex flex-col gap-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">

                <Field
                    name="groundImpedanceL1"
                    validators={{
                        onChange: validators.patchElectricalProjectDistributionBoardCommand.groundImpedanceL1,
                        onChangeAsync: patcher.patchField('GroundImpedanceL1'),
                        onChangeAsyncDebounceMs: patcher.patchField.debounceTime,
                    }}
                >
                    {({ handleChange, state, name }) => (
                        <Label className="grid grid-cols-3 items-center" label={t('distribution-box.measurements.form.L1_PE_O.label')}>
                            <NumberField
                                className="col-span-2 w-full"
                                maxPrecision={2}
                                name={name}
                                onChange={handleChange}
                                value={state.value}
                                placeholder={t('distribution-box.measurements.form.L1_PE_O.placeholder')}
                            />
                            <FieldInfo state={state} />
                        </Label>
                    )}
                </Field>
                <Field
                    name="groundImpedanceCurrentL1"
                    validators={{
                        onChange: validators.patchElectricalProjectDistributionBoardCommand.groundImpedanceCurrentL1,
                        onChangeAsync: patcher.patchField('GroundImpedanceCurrentL1'),
                        onChangeAsyncDebounceMs: patcher.patchField.debounceTime,
                    }}
                >
                    {({ handleChange, state, name }) => (
                        <Label className="grid grid-cols-3 items-center" label={t('distribution-box.measurements.form.L1_PE_A.label')}>
                            <NumberField
                                className="col-span-2 w-full"
                                maxPrecision={0}
                                name={name}
                                onChange={handleChange}
                                value={state.value}
                                placeholder={t('distribution-box.measurements.form.L1_PE_A.placeholder')}
                            />
                            <FieldInfo state={state} />
                        </Label>
                    )}
                </Field>

                {isThreePhase && (
                    <>
                        <Field
                            name="groundImpedanceL2"
                            validators={{
                                onChange: validators.patchElectricalProjectDistributionBoardCommand.groundImpedanceL2,
                                onChangeAsync: patcher.patchField('GroundImpedanceL2'),
                                onChangeAsyncDebounceMs: patcher.patchField.debounceTime,
                            }}
                        >
                            {({ handleChange, state, name }) => (
                                <Label className="grid grid-cols-3 items-center" label={t('distribution-box.measurements.form.L2_PE_O.label')}>
                                    <NumberField
                                        className="col-span-2 w-full"
                                        maxPrecision={2}
                                        name={name}
                                        onChange={handleChange}
                                        value={state.value}
                                        placeholder={t('distribution-box.measurements.form.L2_PE_O.placeholder')}
                                    />
                                    <FieldInfo state={state} />
                                </Label>
                            )}
                        </Field>
                        <Field
                            name="groundImpedanceCurrentL2"
                            validators={{
                                onChange: validators.patchElectricalProjectDistributionBoardCommand.groundImpedanceCurrentL2,
                                onChangeAsync: patcher.patchField('GroundImpedanceCurrentL2'),
                                onChangeAsyncDebounceMs: patcher.patchField.debounceTime,
                            }}
                        >
                            {({ handleChange, state, name }) => (
                                <Label className="grid grid-cols-3 items-center" label={t('distribution-box.measurements.form.L2_PE_A.label')}>
                                    <NumberField
                                        className="col-span-2 w-full"
                                        maxPrecision={0}
                                        name={name}
                                        onChange={handleChange}
                                        value={state.value}
                                        placeholder={t('distribution-box.measurements.form.L2_PE_A.placeholder')}
                                    />
                                    <FieldInfo state={state} />
                                </Label>
                            )}
                        </Field>

                        <Field
                            name="groundImpedanceL3"
                            validators={{
                                onChange: validators.patchElectricalProjectDistributionBoardCommand.groundImpedanceL3,
                                onChangeAsync: patcher.patchField('GroundImpedanceL3'),
                                onChangeAsyncDebounceMs: patcher.patchField.debounceTime,
                            }}
                        >
                            {({ handleChange, state, name }) => (
                                <Label className="grid grid-cols-3 items-center" label={t('distribution-box.measurements.form.L3_PE_O.label')}>
                                    <NumberField
                                        className="col-span-2 w-full"
                                        maxPrecision={2}
                                        name={name}
                                        onChange={handleChange}
                                        value={state.value}
                                        placeholder={t('distribution-box.measurements.form.L3_PE_O.placeholder')}
                                    />
                                    <FieldInfo state={state} />
                                </Label>
                            )}
                        </Field>
                        <Field
                            name="groundImpedanceCurrentL3"
                            validators={{
                                onChange: validators.patchElectricalProjectDistributionBoardCommand.groundImpedanceCurrentL3,
                                onChangeAsync: patcher.patchField('GroundImpedanceCurrentL3'),
                                onChangeAsyncDebounceMs: patcher.patchField.debounceTime,
                            }}
                        >
                            {({ handleChange, state, name }) => (
                                <Label className="grid grid-cols-3 items-center" label={t('distribution-box.measurements.form.L3_PE_A.label')}>
                                    <NumberField
                                        className="col-span-2 w-full"
                                        maxPrecision={0}
                                        name={name}
                                        onChange={handleChange}
                                        value={state.value}
                                        placeholder={t('distribution-box.measurements.form.L3_PE_A.placeholder')}
                                    />
                                    <FieldInfo state={state} />
                                </Label>
                            )}
                        </Field>
                    </>
                )}
            </div>
            <ObservationComponent
                electricalProjectVersionId={params.projectVersionId}
                objectKind="DistributionBoardCircuitImpedance"
                objectId={params.distributionboardId}
            />
        </form>
    );
}

function NetimpedantiesForm({ powerPhaseKind }: { powerPhaseKind: string | null | undefined }) {
    const { t } = useTranslation('re-inspections');
    const isThreePhase = powerPhaseKind === '3';

    const params = Route.useParams();

    const { data } = useQuery(
        elektraAppQueryOptions.project.inspection.distributionBoard.distributionBoardOptions({ electricalProjectDistributionBoardId: params.distributionboardId }),
    );

    const patcher = usePatcher('/api/v2/electrical/projects/distributionboards/patch', {
        onMutate: (variables) => {
            const body = toPascalCase(variables!.body!);

            const distributionBoardQueryOptions = elektraAppQueryOptions.project.inspection.distributionBoard.distributionBoardOptions({ electricalProjectDistributionBoardId: params.distributionboardId });
            const previousDistributionBoardData = queryClient.getQueryData(distributionBoardQueryOptions.queryKey) as components['schemas']['EditElectricalProjectDistributionBoardQueryResult'];
            const newDistributionBoardData: components['schemas']['EditElectricalProjectDistributionBoardQueryResult'] = {
                ...previousDistributionBoardData,
                DistributionBoard: {
                    ...previousDistributionBoardData.DistributionBoard,
                    ...body,
                },
            };

            logger.debug({
                message: 'onMutate(distributionBoardQueryOptions)',
                path: '/api/v2/electrical/projects/distributionboards/patch',
                distributionBoard: {
                    previousData: previousDistributionBoardData,
                    newData: newDistributionBoardData,
                    queryKey: distributionBoardQueryOptions.queryKey,
                    body,
                },
            });

            queryClient.setQueryData(distributionBoardQueryOptions.queryKey, newDistributionBoardData);

            return {
                previousData: previousDistributionBoardData,
                queryKey: distributionBoardQueryOptions.queryKey,
            };
        },
        onSettled: async (_data, _variables, context: any) => {
            await queryClient.invalidateQueries({ queryKey: context.queryKey });
        },
        scope: {
            id: params.projectVersionId,
        },
    }, {
        body: {
            ElectricalProjectDistributionBoardId: params.distributionboardId,
        },
    });

    const { Field, handleSubmit, Subscribe } = useForm<
        NetimpedantiesFormValues,
        ZodValidator
    >({
        defaultValues: {
            neutralImpedanceCurrentL1: data?.DistributionBoard?.NeutralImpedanceCurrentL1 || null,
            neutralImpedanceCurrentL2: data?.DistributionBoard?.NeutralImpedanceCurrentL2 || null,
            neutralImpedanceCurrentL3: data?.DistributionBoard?.NeutralImpedanceCurrentL3 || null,
            neutralImpedanceL1: data?.DistributionBoard?.NeutralImpedanceL1 || null,
            neutralImpedanceL2: data?.DistributionBoard?.NeutralImpedanceL2 || null,
            neutralImpedanceL3: data?.DistributionBoard?.NeutralImpedanceL3 || null,
            measureImpedanceBetweenPowerPhases: data?.DistributionBoard?.MeasureImpedanceBetweenPowerPhases || false,
            impedanceCurrentL1L2: data?.DistributionBoard?.ImpedanceCurrentL1L2 || null,
            impedanceCurrentL1L3: data?.DistributionBoard?.ImpedanceCurrentL1L3 || null,
            impedanceCurrentL2L3: data?.DistributionBoard?.ImpedanceCurrentL2L3 || null,
            impedanceL1L2: data?.DistributionBoard?.ImpedanceL1L2 || null,
            impedanceL1L3: data?.DistributionBoard?.ImpedanceL1L3 || null,
            impedanceL2L3: data?.DistributionBoard?.ImpedanceL2L3 || null,
        },
        validatorAdapter: zodValidator(),
    });

    async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault();
        await handleSubmit();
    }

    return (
        <form onSubmit={onSubmit} className="flex flex-col gap-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">

                <Field
                    name="neutralImpedanceL1"
                    validators={{
                        onChange: validators.patchElectricalProjectDistributionBoardCommand.neutralImpedanceL1,
                        onChangeAsync: patcher.patchField('NeutralImpedanceL1'),
                        onChangeAsyncDebounceMs: patcher.patchField.debounceTime,
                    }}
                >
                    {({ handleChange, state, name }) => (
                        <Label className="grid grid-cols-3 items-center" label={t('distribution-box.measurements.form.L1_N_O.label')}>
                            <NumberField
                                className="col-span-2 w-full"
                                maxPrecision={2}
                                name={name}
                                onChange={handleChange}
                                value={state.value}
                                placeholder={t('distribution-box.measurements.form.L1_N_O.placeholder')}
                            />
                            <FieldInfo state={state} />
                        </Label>
                    )}
                </Field>
                <Field
                    name="neutralImpedanceCurrentL1"
                    validators={{
                        onChange: validators.patchElectricalProjectDistributionBoardCommand.neutralImpedanceCurrentL1,
                        onChangeAsync: patcher.patchField('NeutralImpedanceCurrentL1'),
                        onChangeAsyncDebounceMs: patcher.patchField.debounceTime,
                    }}
                >
                    {({ handleChange, state, name }) => (
                        <Label className="grid grid-cols-3 items-center" label={t('distribution-box.measurements.form.L1_N_A.label')}>
                            <NumberField
                                className="col-span-2 w-full"
                                maxPrecision={0}
                                name={name}
                                onChange={handleChange}
                                value={state.value}
                                placeholder={t('distribution-box.measurements.form.L1_N_A.placeholder')}
                            />
                            <FieldInfo state={state} />
                        </Label>
                    )}
                </Field>

                {isThreePhase && (
                    <>
                        <Field
                            name="neutralImpedanceL2"
                            validators={{
                                onChange: validators.patchElectricalProjectDistributionBoardCommand.neutralImpedanceL2,
                                onChangeAsync: patcher.patchField('NeutralImpedanceL2'),
                                onChangeAsyncDebounceMs: patcher.patchField.debounceTime,
                            }}
                        >
                            {({ handleChange, state, name }) => (
                                <Label className="grid grid-cols-3 items-center" label={t('distribution-box.measurements.form.L2_N_O.label')}>
                                    <NumberField
                                        className="col-span-2 w-full"
                                        maxPrecision={2}
                                        name={name}
                                        onChange={handleChange}
                                        value={state.value}
                                        placeholder={t('distribution-box.measurements.form.L2_N_O.placeholder')}
                                    />
                                    <FieldInfo state={state} />
                                </Label>
                            )}
                        </Field>
                        <Field
                            name="neutralImpedanceCurrentL2"
                            validators={{
                                onChange: validators.patchElectricalProjectDistributionBoardCommand.neutralImpedanceCurrentL2,
                                onChangeAsync: patcher.patchField('NeutralImpedanceCurrentL2'),
                                onChangeAsyncDebounceMs: patcher.patchField.debounceTime,
                            }}
                        >
                            {({ handleChange, state, name }) => (
                                <Label className="grid grid-cols-3 items-center" label={t('distribution-box.measurements.form.L2_N_A.label')}>
                                    <NumberField
                                        className="col-span-2 w-full"
                                        maxPrecision={0}
                                        name={name}
                                        onChange={handleChange}
                                        value={state.value}
                                        placeholder={t('distribution-box.measurements.form.L2_N_A.placeholder')}
                                    />
                                    <FieldInfo state={state} />
                                </Label>
                            )}
                        </Field>

                        <Field
                            name="neutralImpedanceL3"
                            validators={{
                                onChange: validators.patchElectricalProjectDistributionBoardCommand.neutralImpedanceL3,
                                onChangeAsync: patcher.patchField('NeutralImpedanceL3'),
                                onChangeAsyncDebounceMs: patcher.patchField.debounceTime,
                            }}
                        >
                            {({ handleChange, state, name }) => (
                                <Label className="grid grid-cols-3 items-center" label={t('distribution-box.measurements.form.L3_N_O.label')}>
                                    <NumberField
                                        className="col-span-2 w-full"
                                        maxPrecision={2}
                                        name={name}
                                        onChange={handleChange}
                                        value={state.value}
                                        placeholder={t('distribution-box.measurements.form.L3_N_O.placeholder')}
                                    />
                                    <FieldInfo state={state} />
                                </Label>
                            )}
                        </Field>
                        <Field
                            name="neutralImpedanceCurrentL3"
                            validators={{
                                onChange: validators.patchElectricalProjectDistributionBoardCommand.neutralImpedanceCurrentL3,
                                onChangeAsync: patcher.patchField('NeutralImpedanceCurrentL3'),
                                onChangeAsyncDebounceMs: patcher.patchField.debounceTime,
                            }}
                        >
                            {({ handleChange, state, name }) => (
                                <Label className="grid grid-cols-3 items-center" label={t('distribution-box.measurements.form.L3_N_A.label')}>
                                    <NumberField
                                        className="col-span-2 w-full"
                                        maxPrecision={0}
                                        name={name}
                                        onChange={handleChange}
                                        value={state.value}
                                        placeholder={t('distribution-box.measurements.form.L3_N_A.placeholder')}
                                    />
                                    <FieldInfo state={state} />
                                </Label>
                            )}
                        </Field>
                    </>
                )}
            </div>
            {isThreePhase && (
                <>
                    <Field
                        name="measureImpedanceBetweenPowerPhases"
                        validators={{
                            onChange: validators.patchElectricalProjectDistributionBoardCommand.measureImpedanceBetweenPowerPhases,
                            onChangeAsync: patcher.patchField('MeasureImpedanceBetweenPowerPhases'),
                            onChangeAsyncDebounceMs: patcher.patchField.debounceTime,
                        }}
                    >
                        {({ handleChange, state, name }) => (
                            <Label label={t('distribution-box.measurements.form.measure-impedance-between-power-phases.label')} htmlFor={name}>
                                <SingleOptionField
                                    name={name}
                                    handleChange={handleChange}
                                    state={state}
                                    className="flex flex-row items-center justify-between"
                                    options={[
                                        {
                                            label: t('words.yes', { ns: 'common' }),
                                            value: true,
                                        },
                                        {
                                            label: t('words.no', { ns: 'common' }),
                                            value: false,
                                        },
                                    ]}
                                    render={SingleHorizontalOptionItem}
                                />
                                <FieldInfo state={state} />
                            </Label>
                        )}
                    </Field>
                    <Subscribe
                        selector={state => [
                            state.values.measureImpedanceBetweenPowerPhases,
                        ]}
                    >
                        {([measureImpedanceBetweenPowerPhases]) =>
                            measureImpedanceBetweenPowerPhases && (
                                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">

                                    <Field
                                        name="impedanceL1L2"
                                        validators={{
                                            onChange: validators.patchElectricalProjectDistributionBoardCommand.impedanceL1L2,
                                            onChangeAsync: patcher.patchField('ImpedanceL1L2'),
                                            onChangeAsyncDebounceMs: patcher.patchField.debounceTime,
                                        }}
                                    >
                                        {({ handleChange, state, name }) => (
                                            <Label className="grid grid-cols-3 items-center" label={t('distribution-box.measurements.form.impedance-L1-L2.label')}>
                                                <NumberField
                                                    className="col-span-2 w-full"
                                                    maxPrecision={2}
                                                    name={name}
                                                    onChange={handleChange}
                                                    value={state.value}
                                                    placeholder={t('distribution-box.measurements.form.impedance-L1-L2.placeholder')}
                                                />
                                                <FieldInfo state={state} />
                                            </Label>
                                        )}
                                    </Field>
                                    <Field
                                        name="impedanceCurrentL1L2"
                                        validators={{
                                            onChange: validators.patchElectricalProjectDistributionBoardCommand.impedanceCurrentL1L2,
                                            onChangeAsync: patcher.patchField('ImpedanceCurrentL1L2'),
                                            onChangeAsyncDebounceMs: patcher.patchField.debounceTime,
                                        }}
                                    >
                                        {({ handleChange, state, name }) => (
                                            <Label className="grid grid-cols-3 items-center" label={t('distribution-box.measurements.form.impedance-current-L1-L2.label')}>
                                                <NumberField
                                                    className="col-span-2 w-full"
                                                    maxPrecision={0}
                                                    name={name}
                                                    onChange={handleChange}
                                                    value={state.value}
                                                    placeholder={t('distribution-box.measurements.form.impedance-current-L1-L2.placeholder')}
                                                />
                                                <FieldInfo state={state} />
                                            </Label>
                                        )}
                                    </Field>

                                    <Field
                                        name="impedanceL1L3"
                                        validators={{
                                            onChange: validators.patchElectricalProjectDistributionBoardCommand.impedanceL1L3,
                                            onChangeAsync: patcher.patchField('ImpedanceL1L3'),
                                            onChangeAsyncDebounceMs: patcher.patchField.debounceTime,
                                        }}
                                    >
                                        {({ handleChange, state, name }) => (
                                            <Label className="grid grid-cols-3 items-center" label={t('distribution-box.measurements.form.impedance-L1-L3.label')}>
                                                <NumberField
                                                    className="col-span-2 w-full"
                                                    maxPrecision={2}
                                                    name={name}
                                                    onChange={handleChange}
                                                    value={state.value}
                                                    placeholder={t('distribution-box.measurements.form.impedance-L1-L3.placeholder')}
                                                />
                                                <FieldInfo state={state} />
                                            </Label>
                                        )}
                                    </Field>
                                    <Field
                                        name="impedanceCurrentL1L3"
                                        validators={{
                                            onChange: validators.patchElectricalProjectDistributionBoardCommand.impedanceCurrentL1L3,
                                            onChangeAsync: patcher.patchField('ImpedanceCurrentL1L3'),
                                            onChangeAsyncDebounceMs: patcher.patchField.debounceTime,
                                        }}
                                    >
                                        {({ handleChange, state, name }) => (
                                            <Label className="grid grid-cols-3 items-center" label={t('distribution-box.measurements.form.impedance-current-L1-L3.label')}>
                                                <NumberField
                                                    className="col-span-2 w-full"
                                                    maxPrecision={0}
                                                    name={name}
                                                    onChange={handleChange}
                                                    value={state.value}
                                                    placeholder={t('distribution-box.measurements.form.impedance-current-L1-L3.placeholder')}
                                                />
                                                <FieldInfo state={state} />
                                            </Label>
                                        )}
                                    </Field>

                                    <Field
                                        name="impedanceL2L3"
                                        validators={{
                                            onChange: validators.patchElectricalProjectDistributionBoardCommand.impedanceL2L3,
                                            onChangeAsync: patcher.patchField('ImpedanceL2L3'),
                                            onChangeAsyncDebounceMs: patcher.patchField.debounceTime,
                                        }}
                                    >
                                        {({ handleChange, state, name }) => (
                                            <Label className="grid grid-cols-3 items-center" label={t('distribution-box.measurements.form.impedance-L2-L3.label')}>
                                                <NumberField
                                                    className="col-span-2 w-full"
                                                    maxPrecision={2}
                                                    name={name}
                                                    onChange={handleChange}
                                                    value={state.value}
                                                    placeholder={t('distribution-box.measurements.form.impedance-L2-L3.placeholder')}
                                                />
                                                <FieldInfo state={state} />
                                            </Label>
                                        )}
                                    </Field>
                                    <Field
                                        name="impedanceCurrentL2L3"
                                        validators={{
                                            onChange: validators.patchElectricalProjectDistributionBoardCommand.impedanceCurrentL2L3,
                                            onChangeAsync: patcher.patchField('ImpedanceCurrentL2L3'),
                                            onChangeAsyncDebounceMs: patcher.patchField.debounceTime,
                                        }}
                                    >
                                        {({ handleChange, state, name }) => (
                                            <Label className="grid grid-cols-3 items-center" label={t('distribution-box.measurements.form.impedance-current-L2-L3.label')}>
                                                <NumberField
                                                    className="col-span-2 w-full"
                                                    maxPrecision={0}
                                                    name={name}
                                                    onChange={handleChange}
                                                    value={state.value}
                                                    placeholder={t('distribution-box.measurements.form.impedance-current-L2-L3.placeholder')}
                                                />
                                                <FieldInfo state={state} />
                                            </Label>
                                        )}
                                    </Field>
                                </div>
                            )}
                    </Subscribe>
                </>
            )}
            <ObservationComponent
                electricalProjectVersionId={params.projectVersionId}
                objectKind="DistributionBoardNetImpedance"
                objectId={params.distributionboardId}
            />
        </form>
    );
}

function Footer() {
    return (
        <Section.Footer>
            <FooterNavigation />
        </Section.Footer>
    );
}
